import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  List,
  ListItemButton,
  Divider,
} from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export interface CommonMessage {
  id: string | number;
  type: 'user' | 'ai';
  content: string;

  // DoubtAssistantPage ke liye optional fields
  diagram_mermaid?: string;
  diagram_title?: string;
  test_url?: string;
  difficulty_buttons?: string[];
  concept_questions?: string[];
  image_url?: string | string[];
  video_scenes?: any[];
  video_title?: string;
  created_at?: string;

  // Studio ke liye optional
  presentation?: any | null;
}

export interface CommonChatBubbleProps {
  message: CommonMessage;
  aiAvatar?: React.ReactNode;
  userAvatar?: React.ReactNode;
  userLabel?: string;
  renderAiContent?: (content: string) => React.ReactNode;

  renderUserContent?: (content: string) => React.ReactNode;

  diagramSlot?: React.ReactNode;

  /**
   * AnimatedVideoPlayer agar use karna ho.
   */
  videoSlot?: React.ReactNode;

  /**
   * Image content (already rendered ImagePreview etc.)
   */
  imageSlot?: React.ReactNode;

  /**
   * Presentation slot (Studio ke liye PresentationViewer).
   */
  presentationSlot?: React.ReactNode;

  // ── Event handlers (DoubtAssistantPage ke liye) ───────────────────────────

  onConceptClick?: (question: string) => void;
  onDifficultySelect?: (difficulty: 'easy' | 'medium' | 'hard') => void;

  // ── Display options ────────────────────────────────────────────────────────

  /** Timestamp format karne ka function */
  formatTime?: (dateString: string) => string;

  /** Bubble style variant */
  variant?: 'default' | 'studio';
}

// ─────────────────────────────────────────────────────────────────────────────
// Default helpers
// ─────────────────────────────────────────────────────────────────────────────

const defaultFormatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const d = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

// ─────────────────────────────────────────────────────────────────────────────
// Difficulty button colors
// ─────────────────────────────────────────────────────────────────────────────

const difficultyConfig = {
  easy: { bg: '#0077b6', hover: '#0096c7' },
  medium: { bg: '#fb8b24', hover: '#f6bd60' },
  hard: { bg: '#ef4444', hover: '#dc2626' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Typing/loading dots (Studio style)
// ─────────────────────────────────────────────────────────────────────────────

export const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          opacity: 0.5,
          animation: 'cbBounce 1.2s infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes cbBounce': {
            '0%, 80%, 100%': { transform: 'scale(1)', opacity: 0.5 },
            '40%': { transform: 'scale(1.4)', opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main CommonChatBubble
// ─────────────────────────────────────────────────────────────────────────────

const CommonChatBubble: React.FC<CommonChatBubbleProps> = ({
  message: msg,
  aiAvatar,
  userAvatar,
  userLabel = 'U',
  renderAiContent,
  renderUserContent,
  diagramSlot,
  videoSlot,
  imageSlot,
  presentationSlot,
  onConceptClick,
  onDifficultySelect,
  formatTime = defaultFormatTime,
  variant = 'default',
}) => {
  const isUser = msg.type === 'user';

  // Video-only mode: agar sirf video hai, text aur baaki cheezein mat dikhaao
  const hasVideo = Boolean(videoSlot && msg.video_scenes?.length);
  const shouldOnlyShowVideo = hasVideo;

  // ── Avatars ──────────────────────────────────────────────────────────────

  const defaultAiAvatar = (
    <Box sx={{
      width: 32, height: 32, borderRadius: '10px',
      bgcolor: 'primary.main', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 16 }} />
    </Box>
  );

  const defaultUserAvatar = (
    <Avatar
      sx={{
        width: 32,
        height: 32,
        bgcolor: '#e8e8f0',
        fontSize: 13,
        fontWeight: 700,
        color: 'primary.main',
        flexShrink: 0,
      }}
    >
      {userLabel?.[0]?.toUpperCase() || 'U'}
    </Avatar>
  );

  // ── Bubble sx ──────────────────────────────────────────────────────────────

  const bubbleSx =
    variant === 'studio'
      ? {
        px: 2,
        py: 1.4,
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        bgcolor: isUser ? 'primary.main' : '#fff',
        color: isUser ? '#fff' : '#1a1a2e',
        boxShadow: isUser
          ? '0 4px 14px rgba(73,98,229,0.3)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        border: isUser ? 'none' : '1px solid #ebebf0',
        maxWidth: '72%',
        position: 'relative' as const,
      }
      : {
        p: shouldOnlyShowVideo ? 0 : { xs: 1.5, md: 2 },
        borderRadius: 3,
        bgcolor: shouldOnlyShowVideo
          ? 'transparent'
          : isUser
            ? '#667eea'
            : '#fff',
        color: isUser ? '#fff' : '#2c3e50',
        boxShadow: shouldOnlyShowVideo
          ? 'none'
          : '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: { xs: '85%', md: '75%' },
        overflowX: 'hidden',
        wordBreak: 'break-word' as const,
        overflowWrap: 'anywhere' as const,
        whiteSpace: 'normal' as const,
        overflow: 'visible',
        position: 'relative' as const,
      };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'flex-end',
        gap: 1,
      }}
    >
      {/* AI Avatar — left side */}
      {!isUser && (aiAvatar ?? defaultAiAvatar)}

      <Box sx={variant === 'studio' ? { maxWidth: '72%', width: '100%',
          justifyItems: isUser ? 'flex-end' : 'flex-start',} : {}}>
        {/* ── Bubble ─────────────────────────────────────────────────────── */}
        <Box
          component={variant === 'default' ? Paper : Box}
          elevation={variant === 'default' ? 0 : undefined}
          sx={bubbleSx}
        >
          {/* ── Presentation (Studio) ─────────────────────────────────── */}
          {presentationSlot && !isUser ? (
            presentationSlot
          ) : (
            <>
              {/* ── AI message content ──────────────────────────────────── */}
              {!isUser && (
                <>
                  {/* Text content */}
                  {!shouldOnlyShowVideo && msg.content && (
                    <Typography
                      component="div"
                      sx={{
                        fontSize: { xs: 13, md: 14 },
                        lineHeight: 1.6,
                        color: variant === 'studio' ? '#1a1a2e' : undefined,
                      }}
                    >
                      {renderAiContent ? renderAiContent(msg.content) : msg.content}
                    </Typography>
                  )}

                  {/* Diagram slot */}
                  {diagramSlot && (
                    <Box sx={{ mt: 1 }}>{diagramSlot}</Box>
                  )}

                  {/* Video slot */}
                  {hasVideo && (
                    <Box sx={{ width: '100%', mt: !shouldOnlyShowVideo && msg.content ? 1.5 : 0 }}>
                      {videoSlot}
                    </Box>
                  )}

                  {/* Image slot */}
                  {imageSlot && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {imageSlot}
                    </Box>
                  )}

                  {/* Concept questions */}
                  {!shouldOnlyShowVideo && msg.concept_questions && onConceptClick && (
                    <Box sx={{ mt: 2, position: 'relative', zIndex: 10, pointerEvents: 'auto' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                        🤔 You Should also Know:
                      </Typography>
                      <List disablePadding>
                        {msg.concept_questions.map((q, i) => (
                          <Box key={i} sx={{ position: 'relative', zIndex: 11 }}>
                            <ListItemButton
                              sx={{
                                px: 0,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                '&:hover': { backgroundColor: 'rgba(102,126,234,0.08)' },
                              }}
                              onClick={() => onConceptClick(q)}
                            >
                              <SubdirectoryArrowRightIcon sx={{ fontSize: 14 }} />
                              <Typography sx={{ fontSize: 13 }}>{q}</Typography>
                            </ListItemButton>
                            {i !== (msg.concept_questions?.length ?? 0) - 1 && <Divider />}
                          </Box>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Difficulty buttons */}
                  {!shouldOnlyShowVideo && msg.difficulty_buttons && onDifficultySelect && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {(['easy', 'medium', 'hard'] as const).map((d) => (
                        <Button
                          key={d}
                          variant="contained"
                          size="small"
                          onClick={() => onDifficultySelect(d)}
                          sx={{
                            bgcolor: difficultyConfig[d].bg,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: 12, md: 13 },
                            '&:hover': { bgcolor: difficultyConfig[d].hover },
                          }}
                        >
                          {d.charAt(0).toUpperCase() + d.slice(1)}
                        </Button>
                      ))}
                    </Box>
                  )}

                  {/* Test paper link */}
                  {msg.test_url && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
                      <Typography sx={{ fontSize: { xs: 13, md: 14 }, mb: 1, fontWeight: 600 }}>
                        📝 Test Paper Generated!
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        href={msg.test_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          bgcolor: '#10b981',
                          textTransform: 'none',
                          fontSize: { xs: 12, md: 13 },
                          '&:hover': { bgcolor: '#059669' },
                        }}
                      >
                        Open Test Paper
                      </Button>
                    </Box>
                  )}

                  {/* Timestamp */}
                  {msg.created_at && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        color: variant === 'studio' ? '#bbb' : '#9e9e9e',
                        display: 'block',
                        mt: 0.5,
                        textAlign: 'left',
                      }}
                    >
                      {formatTime(msg.created_at)}
                    </Typography>
                  )}
                </>
              )}

              {/* ── User message content ─────────────────────────────────── */}
              {isUser && (
                <>
                  <Typography
                    sx={{
                      fontSize: { xs: 13, md: 14 },
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'inherit',
                      color: '#fff',
                    }}
                  >
                    {renderUserContent
                      ? renderUserContent(msg.content)
                      : typeof msg.content === 'string'
                        ? msg.content
                        : JSON.stringify(msg.content, null, 2)}
                  </Typography>

                  {/* User images */}
                  {imageSlot && (
                    <Box sx={{ mt: msg.content ? 1 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {imageSlot}
                    </Box>
                  )}

                  {/* Timestamp */}
                  {msg.created_at && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        color:
                          variant === 'studio' ? '#bbb' : 'rgba(255,255,255,0.8)',
                        display: 'block',
                        mt: 0.5,
                        textAlign: 'right',
                      }}
                    >
                      {formatTime(msg.created_at)}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        {/* Studio variant: timestamp bahar bubble ke neeche */}
        {variant === 'studio' && msg.created_at && (
          <Typography
            sx={{
              fontSize: 10.5,
              color: '#bbb',
              mt: 0.5,
              textAlign: isUser ? 'right' : 'left',
            }}
          >
            {formatTime(msg.created_at)}
          </Typography>
        )}
      </Box>

      {/* User Avatar — right side */}
      {isUser && (userAvatar ?? defaultUserAvatar)}
    </Box>
  );
};

export default CommonChatBubble;